<template>
  <div class="wise-embed" @keydown.esc="closeMenu">
    <div v-if="showToolbar" class="wise-embed__toolbar">
      <div v-if="companies.length > 1" class="tb-dd">
        <button
          type="button"
          class="tb-ws"
          :disabled="loading"
          @click.stop="toggleMenu('system')"
        >
          <span class="tb-ws-txt">
            <small>Sistema</small>
            <b>{{ selectedCompanyName }}</b>
          </span>
          <svg class="ic ic-sm caret" viewBox="0 0 24 24">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        <div v-if="openMenu === 'system'" class="menu menu-accounts">
          <div class="menu-label">Sistemas</div>
          <label v-if="showCompanySearch" class="acc-search" @click.stop>
            <svg class="ic ic-sm" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              ref="companySearchInput"
              v-model="companyQuery"
              type="text"
              placeholder="Buscar sistema…"
            />
          </label>
          <div class="acc-list">
            <button
              v-for="company in filteredCompanies"
              :key="company.id"
              type="button"
              class="menu-item"
              @click="selectCompany(company.id)"
            >
              <span class="mi-txt">
                <b>{{ companyLabel(company) }}</b>
              </span>
              <svg
                v-if="String(company.id) === String(selectedCompanyId)"
                class="ic ic-sm"
                style="color: #0a7581"
                viewBox="0 0 24 24"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </button>
            <div v-if="!filteredCompanies.length" class="acc-empty">
              Nenhum sistema encontrado
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedUsers.length > 1" class="tb-dd">
        <button
          type="button"
          class="tb-ws"
          :disabled="loading"
          @click.stop="toggleMenu('user')"
        >
          <span class="tb-ws-txt">
            <small>Perfil</small>
            <b>{{ selectedUserName }}</b>
          </span>
          <svg class="ic ic-sm caret" viewBox="0 0 24 24">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        <div v-if="openMenu === 'user'" class="menu menu-accounts">
          <div class="menu-label">Perfis</div>
          <label v-if="showUserSearch" class="acc-search" @click.stop>
            <svg class="ic ic-sm" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              ref="userSearchInput"
              v-model="userQuery"
              type="text"
              placeholder="Buscar perfil…"
            />
          </label>
          <div class="acc-list">
            <button
              v-for="user in filteredUsers"
              :key="user.id"
              type="button"
              class="menu-item"
              @click="selectUser(user.id)"
            >
              <span class="mi-txt">
                <b>{{ userLabel(user) }}</b>
              </span>
              <svg
                v-if="String(user.id) === String(selectedUserId)"
                class="ic ic-sm"
                style="color: #0a7581"
                viewBox="0 0 24 24"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </button>
            <div v-if="!filteredUsers.length" class="acc-empty">
              Nenhum perfil encontrado
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="wise-embed__body">
      <div v-if="loading" class="wise-embed__status">
        <woot-loading-state :message="statusMessage" />
      </div>
      <div v-else-if="errorMessage" class="wise-embed__status">
        <p>{{ errorMessage }}</p>
        <button type="button" class="button nice" @click="loadAndLaunch">
          Tentar novamente
        </button>
      </div>
      <div v-else-if="needsCompanyChoice" class="wise-embed__status">
        <p>Selecione o sistema para abrir o Wise Notification.</p>
      </div>
      <div v-else-if="needsUserChoice" class="wise-embed__status">
        <p>Selecione o perfil Wise para abrir o Wise Notification.</p>
      </div>
      <iframe
        v-else-if="launchUrl"
        ref="wiseFrame"
        :key="frameKey"
        class="wise-embed__frame"
        :src="launchUrl"
        title="Wise Notification"
        referrerpolicy="strict-origin-when-cross-origin"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import WiseSessionAPI from '../../../api/wiseSession';
import {
  angularPathForWiseRoute,
  appendLaunchNext,
  isSafeWiseLaunchUrl,
  isWiseDashboardRoute,
  pickLaunchCompanyId,
  pickLaunchUserId,
  wiseOriginFromLaunchUrl,
  wiseSelectedCompanyKey,
  wiseSelectedUserKey,
} from '../../../helper/wiseLaunch';

const COMPANIES_SEARCH_THRESHOLD = 6;

export default {
  name: 'WiseEmbedHost',
  data() {
    return {
      loading: false,
      statusMessage: 'Abrindo o Wise Notification...',
      errorMessage: '',
      launchUrl: '',
      frameKey: 0,
      companies: [],
      selectedCompanyId: '',
      selectedUserId: '',
      openMenu: null,
      companyQuery: '',
      userQuery: '',
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    onWiseRoute() {
      return isWiseDashboardRoute(this.$route);
    },
    showToolbar() {
      return this.companies.length > 1 || this.selectedUsers.length > 1;
    },
    needsCompanyChoice() {
      return this.companies.length > 1 && !this.selectedCompanyId;
    },
    needsUserChoice() {
      return (
        !!this.selectedCompanyId &&
        this.selectedUsers.length > 1 &&
        !this.selectedUserId
      );
    },
    selectedCompany() {
      return this.companies.find(
        item => String(item.id) === String(this.selectedCompanyId)
      );
    },
    selectedUsers() {
      return this.selectedCompany?.users || [];
    },
    selectedCompanyName() {
      return this.companyLabel(this.selectedCompany) || 'Selecione o sistema';
    },
    selectedUserName() {
      const user = this.selectedUsers.find(
        item => String(item.id) === String(this.selectedUserId)
      );
      return this.userLabel(user) || 'Selecione o perfil';
    },
    showCompanySearch() {
      return this.companies.length > COMPANIES_SEARCH_THRESHOLD;
    },
    showUserSearch() {
      return this.selectedUsers.length > COMPANIES_SEARCH_THRESHOLD;
    },
    filteredCompanies() {
      return this.filterByQuery(
        this.companies,
        this.companyQuery,
        company => `${this.companyLabel(company)} ${company.id}`
      );
    },
    filteredUsers() {
      return this.filterByQuery(
        this.selectedUsers,
        this.userQuery,
        user => `${this.userLabel(user)} ${user.id} ${user.email || ''}`
      );
    },
  },
  watch: {
    '$route.name'(name) {
      if (!this.onWiseRoute || !this.launchUrl || this.loading) {
        return;
      }
      this.navigateIframe(name);
    },
    accountId(next, prev) {
      if (prev && next && next !== prev) {
        this.selectedCompanyId = '';
        this.selectedUserId = '';
        this.closeMenu();
        this.loadAndLaunch();
      }
    },
  },
  created() {
    window.addEventListener('message', this.onWiseMessage);
    this.loadAndLaunch();
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onWiseMessage);
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    storedCompanyId() {
      try {
        return (
          localStorage.getItem(wiseSelectedCompanyKey(this.accountId)) || ''
        );
      } catch (error) {
        return '';
      }
    },
    persistCompanyId(companyId) {
      try {
        localStorage.setItem(wiseSelectedCompanyKey(this.accountId), companyId);
      } catch (error) {
        // ignore quota / private mode
      }
    },
    storedUserId(companyId) {
      try {
        return (
          localStorage.getItem(
            wiseSelectedUserKey(this.accountId, companyId)
          ) || ''
        );
      } catch (error) {
        return '';
      }
    },
    persistUserId(companyId, userId) {
      try {
        localStorage.setItem(
          wiseSelectedUserKey(this.accountId, companyId),
          userId
        );
      } catch (error) {
        // ignore quota / private mode
      }
    },
    toggleMenu(which) {
      this.openMenu = this.openMenu === which ? null : which;
      if (this.openMenu === 'system') {
        this.companyQuery = '';
        this.$nextTick(() => {
          if (this.$refs.companySearchInput) {
            this.$refs.companySearchInput.focus();
          }
        });
      }
      if (this.openMenu === 'user') {
        this.userQuery = '';
        this.$nextTick(() => {
          if (this.$refs.userSearchInput) {
            this.$refs.userSearchInput.focus();
          }
        });
      }
    },
    closeMenu() {
      this.openMenu = null;
      this.companyQuery = '';
      this.userQuery = '';
    },
    onDocumentClick(event) {
      if (!this.$el.contains(event.target) || !event.target.closest('.tb-dd')) {
        this.closeMenu();
      }
    },
    companyLabel(company) {
      if (!company) {
        return '';
      }
      return company.name || company.nameCompany || '';
    },
    userLabel(user) {
      if (!user) {
        return '';
      }
      const name = user.name || '';
      const email = user.email || '';
      if (name && email) {
        return `${name} — ${email}`;
      }
      return name || email || user.id || '';
    },
    filterByQuery(items, query, haystackFor) {
      const tokens = this.normalizeText(query)
        .split(/\s+/)
        .filter(Boolean);
      if (!tokens.length) return items;
      return items.filter(item => {
        const haystack = this.normalizeText(haystackFor(item));
        return tokens.every(token => haystack.includes(token));
      });
    },
    normalizeText(text) {
      return (text || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    },
    async loadAndLaunch() {
      this.loading = true;
      this.errorMessage = '';
      this.launchUrl = '';
      this.statusMessage = 'Abrindo o Wise Notification...';

      try {
        const { data } = await WiseSessionAPI.companies();
        this.companies = data?.companies || [];
        if (!this.companies.length) {
          this.errorMessage =
            'Nenhum sistema Wise está atribuído a este usuário.';
          return;
        }
        const companyId = pickLaunchCompanyId(this.companies, {
          requestedId: this.selectedCompanyId,
          storedId: this.storedCompanyId(),
        });
        this.selectedCompanyId = companyId;
        if (!companyId) {
          this.selectedUserId = '';
          return;
        }
        const userId = pickLaunchUserId(this.selectedUsers, {
          requestedId: this.selectedUserId,
          storedId: this.storedUserId(companyId),
        });
        this.selectedUserId = userId;
        if (this.selectedUsers.length > 1 && !userId) {
          return;
        }
        await this.launchWise(companyId, userId);
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          'Não foi possível abrir o Wise Notification.';
      } finally {
        this.loading = false;
      }
    },
    async launchWise(companyId, wiseUserId) {
      this.loading = true;
      this.errorMessage = '';
      this.statusMessage = 'Abrindo o Wise Notification...';
      try {
        const { data } = await WiseSessionAPI.launch(companyId, wiseUserId);
        const url = data?.launch_url;
        if (!url || !isSafeWiseLaunchUrl(url)) {
          throw new Error('missing launch url');
        }
        this.persistCompanyId(companyId);
        if (wiseUserId) {
          this.persistUserId(companyId, wiseUserId);
        }
        this.frameKey += 1;
        this.launchUrl = appendLaunchNext(
          url,
          angularPathForWiseRoute(this.$route.name)
        );
      } catch (error) {
        this.launchUrl = '';
        this.errorMessage =
          error?.response?.data?.error ||
          'Não foi possível abrir o Wise Notification.';
      } finally {
        this.loading = false;
      }
    },
    selectCompany(companyId) {
      if (!companyId) {
        this.closeMenu();
        return;
      }
      const sameSystem =
        String(companyId) === String(this.selectedCompanyId);
      this.selectedCompanyId = companyId;
      this.closeMenu();
      if (sameSystem && this.launchUrl) {
        return;
      }
      const userId = pickLaunchUserId(this.selectedUsers, {
        storedId: this.storedUserId(companyId),
      });
      this.selectedUserId = userId;
      if (this.selectedUsers.length > 1 && !userId) {
        this.launchUrl = '';
        return;
      }
      this.launchWise(companyId, userId);
    },
    selectUser(userId) {
      if (!userId) {
        this.closeMenu();
        return;
      }
      const sameUser = String(userId) === String(this.selectedUserId);
      this.selectedUserId = userId;
      this.closeMenu();
      if (sameUser && this.launchUrl) {
        return;
      }
      this.launchWise(this.selectedCompanyId, userId);
    },
    navigateIframe(routeName) {
      const frame = this.$refs.wiseFrame;
      const origin = wiseOriginFromLaunchUrl(this.launchUrl);
      if (!frame || !frame.contentWindow || !origin) {
        return;
      }
      frame.contentWindow.postMessage(
        {
          type: 'omni:navigate',
          path: angularPathForWiseRoute(routeName),
        },
        origin
      );
    },
    onWiseMessage(event) {
      const origin = wiseOriginFromLaunchUrl(this.launchUrl);
      if (!origin || event.origin !== origin) {
        return;
      }
      if (event.data && event.data.type === 'wise:auth-expired') {
        if (this.loading) {
          return;
        }
        this.loadAndLaunch();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$surface: #ffffff;
$surface-2: #f0f3f5;
$border: #e4e9ee;
$text: #0f1f2a;
$text-3: #8a97a3;
$brand: #01c6d8;
$brand-050: #e1f8fb;

.wise-embed {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: visible;
  z-index: 2;
  background: $surface;
}

.wise-embed__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  position: relative;
  z-index: 0;
}

.wise-embed__toolbar {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: var(--space-small);
  padding: 0 var(--space-normal);
  height: 56px;
  border-bottom: 1px solid $border;
  background: $surface;
  color: $text;
  position: relative;
  z-index: 3;
}

.tb-dd {
  position: relative;
}

.ic {
  width: 20px;
  height: 20px;
  flex: none;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.ic-sm {
  width: 16px;
  height: 16px;
}

.tb-ws {
  display: flex;
  align-items: center;
  gap: var(--space-one);
  padding: 5px 10px 5px 6px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: $text;
  transition: background 0.15s ease-in-out;
  flex: none;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: $surface-2;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }

  .caret {
    color: $text-3;
  }
}

.tb-ws-txt {
  text-align: left;
  line-height: 1.15;

  small {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: $text-3;
    display: block;
  }

  b {
    font-size: 13px;
    font-weight: 700;
    color: $text;
  }
}

.menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 25rem;
  background: $surface;
  border: 1px solid $border;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(16, 32, 45, 0.16);
  padding: 6px;
  z-index: 70;
  color: $text;
  animation: menuIn 0.14s ease;
}

@keyframes menuIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.menu-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: $text-3;
  padding: 8px 10px 4px;
}

.menu-accounts {
  min-width: 28rem;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 8rem);
}

.acc-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 4px 6px;
  padding: 0 10px;
  height: 34px;
  background: $surface-2;
  border: 1px solid $border;
  border-radius: 8px;
  cursor: text;

  &:focus-within {
    background: $surface;
    border-color: $brand;
    box-shadow: 0 0 0 3px $brand-050;
  }

  svg {
    color: $text-3;
    flex: none;
  }

  input {
    flex: 1;
    min-width: 0;
    border: none;
    background: none;
    outline: none;
    color: $text;
    font-size: 1.3rem;
    margin: 0;
    height: auto;
    box-shadow: none !important;

    &::placeholder {
      color: $text-3;
    }
  }
}

.acc-list {
  overflow-y: auto;
  max-height: 34rem;
  margin: 0 -2px;
  padding: 0 2px;
}

.acc-empty {
  padding: 16px 10px;
  text-align: center;
  font-size: 12px;
  color: $text-3;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  color: $text;
  width: 100%;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  transition: background 0.12s ease-in-out;

  &:hover {
    background: $surface-2;
  }

  .mi-txt {
    flex: 1;
    min-width: 0;

    b {
      font-weight: 600;
      display: block;
    }
  }
}

.wise-embed__frame {
  flex: 1;
  width: 100%;
  height: 100%;
  border: 0;
  background: $surface;
}

.wise-embed__status {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  width: 100%;
  text-align: center;
}
</style>
